import React, { useMemo } from "react";

import Card from "../../ui/Card";
import { aggregateAuditSegments, isGenericAuditSegment } from "./auditSegmentUtils";

function compactPercent(value) {
  if (value == null || Number.isNaN(Number(value))) return 0;
  return Math.round(Number(value));
}

function pct(value) {
  if (value == null || Number.isNaN(Number(value))) return "0%";
  return `${Math.round(Number(value))}%`;
}

function rankedBy(rows, scoreFn) {
  return [...(rows || [])].sort((a, b) => {
    const delta = scoreFn(b) - scoreFn(a);
    if (delta !== 0) return delta;
    return (b.count ?? 0) - (a.count ?? 0);
  });
}

function RadialMetric({ value }) {
  const score = Math.min(100, Math.max(0, Number(value) || 0));
  const size = 42;
  const stroke = 5;
  const radius = size / 2 - stroke / 2;
  const circum = 2 * Math.PI * radius;

  return (
    <div className="relative h-[42px] w-[42px] shrink-0">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className="stroke-theme-border"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className="stroke-theme-primary"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(score / 100) * circum} ${circum}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-mono text-[9px] font-semibold text-theme-on-background tabular-nums">
        {Math.round(score)}%
      </div>
    </div>
  );
}

function RunnerUpLine({ rows, metricLabel, valueFn }) {
  const extras = rows.slice(1, 3).filter((row) => valueFn(row) > 0);
  if (!extras.length) return null;
  return (
    <div className="mt-1 font-mono text-[10px] text-theme-muted leading-snug">
      Also notable:{" "}
      {extras.map((row, index) => (
        <span key={row.segment}>
          {index > 0 ? ", " : ""}
          {row.segment} ({metricLabel(valueFn(row))})
        </span>
      ))}
    </div>
  );
}

export default function AuditOpportunityInsights({ rows, dimensionLabel }) {
  const insights = useMemo(() => {
    const labeledRows = aggregateAuditSegments(rows).filter((row) => !isGenericAuditSegment(row.segment));
    const total = labeledRows.reduce((sum, row) => sum + (row.count ?? 0), 0);
    const totalMatches = labeledRows.reduce((sum, row) => sum + (row.icp_match_count ?? 0), 0);
    const withMatches = labeledRows.filter((row) => (row.icp_match_count ?? 0) > 0);
    const byShare = rankedBy(labeledRows, (row) => row.count ?? 0);
    const byMatches = rankedBy(withMatches, (row) => row.icp_match_count ?? 0);
    const byRate = rankedBy(withMatches, (row) => row.icp_match_rate_pct ?? 0);
    const byFit = rankedBy(
      withMatches.filter((row) => row.avg_match_score != null),
      (row) => row.avg_match_score ?? 0
    );

    return [
      {
        eyebrow: "Mailbox share",
        row: byShare[0],
        rows: byShare,
        value: byShare[0] ? compactPercent(byShare[0].share_pct) : 0,
        title: byShare[0] ? `${byShare[0].segment} is the biggest ${dimensionLabel.toLowerCase()} category` : `${dimensionLabel} mix pending`,
        body: byShare[0]
          ? `${byShare[0].count} of ${total} categorized cold emails are here, ${pct(byShare[0].share_pct)} of this view.`
          : `No named ${dimensionLabel.toLowerCase()} categories are ready to summarize yet.`,
        runnerValue: (row) => row.share_pct ?? 0,
        runnerLabel: pct,
      },
      {
        eyebrow: "Most matches",
        row: byMatches[0],
        rows: byMatches,
        value: totalMatches ? compactPercent((100 * (byMatches[0]?.icp_match_count ?? 0)) / totalMatches) : 0,
        title: byMatches[0] ? `${byMatches[0].segment} has the most ICP matches` : "Matches are still sparse",
        body: byMatches[0]
          ? `${byMatches[0].icp_match_count} matches came from this category, out of ${totalMatches} matches in the ${dimensionLabel.toLowerCase()} view.`
          : `${totalMatches} emails in this ${dimensionLabel.toLowerCase()} view met your ICP match criteria.`,
        runnerValue: (row) => row.icp_match_count ?? 0,
        runnerLabel: (value) => `${value} matches`,
      },
      {
        eyebrow: "Match rate",
        row: byRate[0],
        rows: byRate,
        value: compactPercent(byRate[0]?.icp_match_rate_pct),
        title: byRate[0] ? `${byRate[0].segment} converts at the highest rate` : "Match rate pending",
        body: byRate[0]
          ? `${byRate[0].icp_match_count} of ${byRate[0].count} emails matched, a ${pct(byRate[0].icp_match_rate_pct)} match rate.`
          : "Once more cold outreach is categorized, this will show which category converts best.",
        runnerValue: (row) => row.icp_match_rate_pct ?? 0,
        runnerLabel: pct,
      },
      {
        eyebrow: "Highest fit",
        row: byFit[0],
        rows: byFit,
        value: compactPercent(byFit[0]?.avg_match_score),
        title: byFit[0] ? `${byFit[0].segment} has the highest average fit` : "Fit score pending",
        body: byFit[0]
          ? `Matched emails in this category average ${pct(byFit[0].avg_match_score)} overall fit.`
          : `As more ${dimensionLabel.toLowerCase()} data is categorized, this will show the strongest average-fit category.`,
        runnerValue: (row) => row.avg_match_score ?? 0,
        runnerLabel: pct,
      },
    ];
  }, [dimensionLabel, rows]);

  return (
    <Card className="p-4 md:p-5 h-full">
      <h3 className="font-display font-bold italic text-lg text-theme-on-background mb-1">ICP Match Insights</h3>
      <p className="font-mono text-[10px] text-theme-dim mb-4 leading-snug">
        Metrics recalculated for the selected {dimensionLabel.toLowerCase()} view.
      </p>
      <div className="space-y-4">
        {insights.map((insight) => (
          <div key={insight.eyebrow} className="flex gap-3 border-t border-theme-border pt-3 first:border-t-0 first:pt-0">
            <RadialMetric value={insight.value} />
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-theme-dim mb-2">
                {insight.eyebrow}
              </div>
              <h3 className="font-display font-bold italic text-base text-theme-on-background leading-tight mb-2">
                {insight.title}
              </h3>
              <p className="text-theme-muted text-xs leading-relaxed">{insight.body}</p>
              <RunnerUpLine rows={insight.rows} metricLabel={insight.runnerLabel} valueFn={insight.runnerValue} />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
